import DownloadDoneIcon from '@mui/icons-material/DownloadDone';
import { Alert, Box, Collapse, IconButton, Tooltip } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    patchTestResultRequest,
    patchTestResultReset,
    patchTestRunRequest,
    patchTestRunReset,
    patchTestRunResultsRequest,
    patchTestRunResultsReset,
} from '@pages/testruns/store';
import { REQUEST_STATUS, utmsButtonSx } from '@shared/constants/otherConstants';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import {
    selectPatchTestResultStatus,
    selectPatchTestRunErrorInfo,
    selectPatchTestRunResultsErrorInfo,
    selectPatchTestRunResultsStatus,
    selectPatchTestRunStatus,
    selectTestResult,
    selectTestResults,
} from '@pages/testruns/store/selectors';
import { LoadingButton } from '@mui/lab';
import JiraIcon from '@shared/static/icons/JiraIcon';
import { AUTO_TEST_RESULT, HAND_TEST_RESULT } from '@pages/testruns/constants/testrunsConstants';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TestResultChip from './TestResultChip';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { getStateFromUrlParams } from '@shared/utils/utilsOld';
import { useURLParams } from '@shared/hooks/useURLParams';
import DeployFilters from '@pages/stands/components/DeployModal/DeployFilters';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ModalSuccessContainer from '@shared/components/modals/ModalSuccessContainer';
import DefectsListManager from '@pages/defects/components/DefectsListManager';
import useDefectLinkManager from '@pages/defects/hooks/useDefectLinkManager';
import { isEqual } from 'lodash';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import TestrunJiraBugsTable from './TestrunJiraBugsTable';

const JIRA_BUG_LINK_TEMPLATE = 'https://jira.esafeline.com/browse/';

const TestrunResultModifyDialogWithButton = (props) => {
    const {
        testrunId,
        testresultId,
        selectedTestRunResultsObjects,
        disabled,
        disableReason,
        onlyJiraBugs,
        jiraBugsWithCount,
        readonlyMode,
        createdAtLast,
        verifyResults,
        verifyTestPlan,
    } = props;
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    const patchTestRunResultsStatus = useSelector(selectPatchTestRunResultsStatus);
    const patchTestResultStatus = useSelector(selectPatchTestResultStatus);
    const testresults = useSelector(selectTestResults);
    const [urlParams] = useURLParams();
    const [pagination, setPagination] = useState(getStateFromUrlParams('pagination', urlParams));
    const finalSelectedObjects = useMemo(() => {
        if (onlyJiraBugs && selectedTestRunResultsObjects?.length > 0) {
            return selectedTestRunResultsObjects;
        }
        return verifyResults ? selectedTestRunResultsObjects : testresults;
    }, [onlyJiraBugs, selectedTestRunResultsObjects, verifyResults, testresults]);
    const jiraBugsCount = useMemo(() => {
        return finalSelectedObjects?.[0]?.jira_bugs?.length || 0;
    }, [finalSelectedObjects]);
    const patchTestRunStatus = useSelector(selectPatchTestRunStatus);

    useEffect(() => {
        const newPagination = getStateFromUrlParams('pagination', urlParams);
        setPagination(newPagination);
    }, [urlParams]);

    const handleOpenDialog = () => {
        setOpen(true);
    };

    const handleSaveTestrunResult = ({ jiraBugs, comments, result, defectsToToggleLink }) => {
        const data = { jira_bugs: jiraBugs, defects_ids_to_toggle_link: defectsToToggleLink };

        if (!onlyJiraBugs) {
            data.comments = comments;
            data.result = result;
            data.status = 'completed';
        }

        if (testresultId) {
            dispatch(
                patchTestResultRequest({
                    createdAtLast,
                    testresultId,
                    data: onlyJiraBugs ? { jira_bugs: jiraBugs } : data,
                    page_index: pagination.pageIndex,
                    page_size: pagination.pageSize,
                }),
            );
        } else if (testrunId) {
            if (verifyTestPlan) {
                dispatch(
                    patchTestRunRequest({
                        testrunId,
                        data,
                    }),
                );
            } else {
                dispatch(
                    patchTestRunResultsRequest({
                        testrunId,
                        createdAtLast,
                        results: selectedTestRunResultsObjects,
                        data,
                    }),
                );
            }
        }

        handleCloseDialog();
    };

    const handleCloseDialog = () => {
        setOpen(false);
    };

    return (
        <>
            <Tooltip title={disableReason}>
                <span>
                    <LoadingButton
                        startIcon={onlyJiraBugs ? <JiraIcon disabled={disabled} /> : <DownloadDoneIcon />}
                        sx={utmsButtonSx}
                        size="small"
                        disabled={disabled}
                        loading={
                            (testresultId ? patchTestResultStatus : patchTestRunResultsStatus) ===
                            REQUEST_STATUS.LOADING
                        }
                        loadingPosition="start"
                        onClick={handleOpenDialog}
                    >
                        {onlyJiraBugs
                            ? jiraBugsWithCount
                                ? t('jira_bugsWithCount', {
                                      count: jiraBugsCount,
                                  })
                                : t('jira_bugs')
                            : t(`${readonlyMode ? 'view' : 'verify'}_result`)}
                    </LoadingButton>
                </span>
            </Tooltip>
            <TestrunResultModifyDialog
                {...props}
                selectedTestRunResultsObjects={finalSelectedObjects}
                open={open}
                setOpen={setOpen}
                handleSave={handleSaveTestrunResult}
            />
            {patchTestRunStatus === REQUEST_STATUS.SUCCESS && (
                <ModalSuccessContainer
                    object={''}
                    action={t('result_verification')}
                    resetActionCallback={patchTestRunReset}
                />
            )}
            {patchTestRunStatus === REQUEST_STATUS.ERROR && (
                <ModalErrorContainer
                    object={''}
                    action={t('verifying_result')}
                    resetActionCallback={patchTestRunReset}
                    errorSelector={selectPatchTestRunErrorInfo}
                />
            )}
            {patchTestRunResultsStatus === REQUEST_STATUS.SUCCESS && (
                <ModalSuccessContainer
                    object={''}
                    action={t('result_verification')}
                    resetActionCallback={patchTestRunResultsReset}
                />
            )}
            {patchTestRunResultsStatus === REQUEST_STATUS.ERROR && (
                <ModalErrorContainer
                    object={''}
                    action={t('verifying_result')}
                    resetActionCallback={patchTestRunResultsReset}
                    errorSelector={selectPatchTestRunResultsErrorInfo}
                />
            )}
            {patchTestResultStatus === REQUEST_STATUS.SUCCESS && (
                <ModalSuccessContainer
                    object={''}
                    action={t('result_verification')}
                    resetActionCallback={patchTestResultReset}
                />
            )}
            {patchTestResultStatus === REQUEST_STATUS.ERROR && (
                <ModalErrorContainer
                    object={''}
                    action={t('verifying_result')}
                    resetActionCallback={patchTestResultReset}
                    errorSelector={selectPatchTestRunResultsErrorInfo}
                />
            )}
        </>
    );
};

const TestrunResultModifyDialog = ({
    open,
    setOpen,
    selectedTestRunResultsObjects,
    onlyJiraBugs,
    readonlyMode,
    forceInitialResult,
    handleSave,
    showDeployFilters,
    selectedFilter,
    setSelectedFilter,
    error,
    hasFailedSteps,
    testrun,
    verifyTestPlan,
}) => {
    const firstTestResultObject = selectedTestRunResultsObjects[0];
    const initialJiraBugsRef = useRef([]);
    const [result, setResult] = useState('');
    const [comments, setComments] = useState('initialComments');
    const [jiraBugs, setJiraBugs] = useState([]);
    const [tmpJiraBug, setTmpJiraBug] = useState('');
    const [addBugError, setAddBugError] = useState(false);
    const onlyOneTestcaseSelected = selectedTestRunResultsObjects.length === 1;
    const bugsAreOnlyAndUnmodified = onlyJiraBugs && isEqual(jiraBugs, initialJiraBugsRef.current);
    const statusFailedAndEmptyJiraBugs = result === AUTO_TEST_RESULT.FAILED.toLowerCase() && !jiraBugs.length;
    const statusBlockedAndCommentsEmpty = result === HAND_TEST_RESULT.BLOCKED.toLowerCase() && comments === '';
    const buildNotSelected = !readonlyMode && showDeployFilters && !selectedFilter.build;
    const branchNotSelected = !readonlyMode && showDeployFilters && !selectedFilter.branch;
    const isHandTestResult =
        onlyOneTestcaseSelected && firstTestResultObject && firstTestResultObject.testcase?.is_handtest;
    const timeoutIdRef = useRef(null);
    const { t } = useTranslation();
    const { defectsArrLocal, defectsToToggleLink, handleLinkDefect, handleUnlinkDefect } = useDefectLinkManager(
        firstTestResultObject?.defects ?? [],
    );

    useEffect(() => {
        if (open && (selectedTestRunResultsObjects.length > 0 || verifyTestPlan)) {
            const first = selectedTestRunResultsObjects[0] || (verifyTestPlan && testrun);
            setResult(forceInitialResult || first.result || '');
            setComments(first.comments || '');
            setJiraBugs(first.jira_bugs || []);
            initialJiraBugsRef.current = first.jira_bugs || [];
        }
    }, [open, selectedTestRunResultsObjects, forceInitialResult, verifyTestPlan, testrun]);

    const handleChangeResult = (e) => {
        setResult(e.target.value);
    };

    const handleChangeTmpJiraBug = (e) => {
        setTmpJiraBug(e.target.value);
    };

    const handleChangeComments = (e) => {
        setComments(e.target.value);
    };

    const handleAddJiraBug = () => {
        const jiraBugToAdd = tmpJiraBug.trim();

        const notJiraLink = !jiraBugToAdd.includes(JIRA_BUG_LINK_TEMPLATE);
        const sameLinkExists = jiraBugs.includes(jiraBugToAdd);

        if (notJiraLink || sameLinkExists) {
            setAddBugError(notJiraLink ? t('not_jira_link_error') : t('same_jira_link_error'));
            timeoutIdRef.current && clearTimeout(timeoutIdRef.current);

            timeoutIdRef.current = setTimeout(() => {
                setAddBugError('');
            }, 3000);
            return;
        }

        setAddBugError('');
        setJiraBugs((existingBugs) => [jiraBugToAdd, ...existingBugs]);
        setTmpJiraBug('');
    };

    const handleDeleteJiraBug = (jiraBugToDelete) => () => {
        setJiraBugs((existingBugs) => existingBugs.filter((bug) => bug !== jiraBugToDelete));
    };

    useEffect(() => {
        return () => clearTimeout(timeoutIdRef.current);
    }, []);

    const handleSaveTestrunResult = () => {
        handleSave({ jiraBugs, comments, result, defectsToToggleLink });
    };

    const selectStatusItems = useMemo(
        () =>
            Object.keys(isHandTestResult ? HAND_TEST_RESULT : AUTO_TEST_RESULT).map((status) => {
                const isDisabled = status === 'PASSED' && hasFailedSteps;

                return (
                    <MenuItem
                        key={status}
                        value={status.toLowerCase()}
                        disableRipple={isDisabled}
                        onClick={(e) => {
                            if (isDisabled) {
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                            }
                        }}
                        sx={{
                            ...(isDisabled && {
                                opacity: 0.6,
                                pointerEvents: 'none',
                                '& > div > div:last-child': {
                                    pointerEvents: 'auto',
                                },
                                '&:hover, &:focus, &:active, &.Mui-focusVisible': {
                                    backgroundColor: 'transparent',
                                },
                            }),
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                                position: 'relative',
                            }}
                        >
                            <TestResultChip disabled={readonlyMode || isDisabled} result={status} />
                            {isDisabled && (
                                <Tooltip
                                    title={t('cannot_select_passed_if_failed_steps_exist')}
                                    slotProps={{
                                        popper: {
                                            onClick: (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            },
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            position: 'absolute',
                                            right: 10,
                                            cursor: 'default',
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                    >
                                        <HelpOutlineIcon
                                            sx={{
                                                fontSize: '18px',
                                                color: 'text',
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                            )}
                        </Box>
                    </MenuItem>
                );
            }),
        [isHandTestResult, readonlyMode, hasFailedSteps, t],
    );

    return (
        (firstTestResultObject || verifyTestPlan) && (
            <>
                <Dialog
                    PaperProps={{
                        sx: {
                            width: onlyJiraBugs ? '640px' : '900px',
                        },
                    }}
                    maxWidth={false}
                    open={open}
                >
                    <DialogTitle variant="h4">
                        {`${onlyJiraBugs ? t('jira_bugs') : t(`${readonlyMode ? 'view' : 'verify'}_result`)} - ${
                            onlyOneTestcaseSelected ? firstTestResultObject.pk : testrun?.testplan?.title || ''
                        }`}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {!onlyJiraBugs && (
                                <>
                                    <FormControl
                                        sx={{ width: '158px', pointerEvents: readonlyMode ? 'none' : 'initial' }}
                                        size="small"
                                        margin="dense"
                                    >
                                        <InputLabel id="result-status-label">{t('status')}</InputLabel>
                                        <Select
                                            labelId="result-status-label"
                                            label={t('status')}
                                            value={result}
                                            onChange={handleChangeResult}
                                            inputProps={{ readOnly: readonlyMode }}
                                        >
                                            {selectStatusItems}
                                        </Select>
                                    </FormControl>
                                    {showDeployFilters && (
                                        <DeployFilters
                                            disabledBuild={!selectedFilter.branch || readonlyMode}
                                            disabledBranch={readonlyMode}
                                            selectedFilter={selectedFilter}
                                            setSelectedFilter={setSelectedFilter}
                                            error={error}
                                            showArchitecture={false}
                                            showIsPublic={false}
                                        />
                                    )}
                                </>
                            )}
                            {!readonlyMode && !onlyJiraBugs && !isHandTestResult && (
                                <DefectsListManager
                                    defects={defectsArrLocal}
                                    onAdd={handleLinkDefect}
                                    onRemove={handleUnlinkDefect}
                                    showList
                                    listMaxHeight={200}
                                />
                            )}
                            {!readonlyMode && (
                                <>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                        <TextField
                                            margin="dense"
                                            fullWidth
                                            size="small"
                                            label={t('jira_link')}
                                            placeholder="URL..."
                                            value={tmpJiraBug}
                                            onChange={handleChangeTmpJiraBug}
                                            error={statusFailedAndEmptyJiraBugs}
                                            helperText={
                                                statusFailedAndEmptyJiraBugs &&
                                                t('this_field_is_required_for_this_result')
                                            }
                                        />
                                        <UtmsButton
                                            disabled={!tmpJiraBug}
                                            startIcon={<AddIcon />}
                                            onClick={handleAddJiraBug}
                                            sx={{ mt: 1 }}
                                        >
                                            {t('add')}
                                        </UtmsButton>
                                    </Box>
                                    <Collapse in={Boolean(addBugError)}>
                                        <Alert
                                            action={
                                                <IconButton
                                                    color="inherit"
                                                    size="small"
                                                    onClick={() => {
                                                        setAddBugError(false);
                                                    }}
                                                >
                                                    <CloseIcon fontSize="inherit" />
                                                </IconButton>
                                            }
                                            severity="error"
                                            size="small"
                                            sx={{ mb: 2 }}
                                        >
                                            {addBugError}
                                        </Alert>
                                    </Collapse>
                                </>
                            )}
                            <Box>
                                <TestrunJiraBugsTable
                                    jiraBugs={jiraBugs}
                                    handleDeleteJiraBug={handleDeleteJiraBug}
                                    readonly={readonlyMode}
                                />
                            </Box>
                            {!onlyJiraBugs && (
                                <TextField
                                    label={t('comments')}
                                    fullWidth
                                    multiline
                                    disabled={readonlyMode}
                                    rows={10}
                                    value={comments}
                                    onChange={handleChangeComments}
                                    error={statusBlockedAndCommentsEmpty}
                                    helperText={
                                        statusBlockedAndCommentsEmpty && t('this_field_is_required_for_this_result')
                                    }
                                />
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        {!readonlyMode && (
                            <UtmsButton
                                disabled={
                                    bugsAreOnlyAndUnmodified ||
                                    statusFailedAndEmptyJiraBugs ||
                                    statusBlockedAndCommentsEmpty ||
                                    buildNotSelected ||
                                    branchNotSelected
                                }
                                onClick={handleSaveTestrunResult}
                            >
                                {t('save')}
                            </UtmsButton>
                        )}
                        <UtmsButton onClick={() => setOpen(false)} color={readonlyMode ? 'primary' : 'secondary'}>
                            {readonlyMode ? t('close') : t('cancel')}
                        </UtmsButton>
                    </DialogActions>
                </Dialog>
            </>
        )
    );
};

export default TestrunResultModifyDialogWithButton;

export const TestrunResultVerifyDialog = (props) => {
    return <TestrunResultModifyDialogWithButton {...props} />;
};

export const TestrunJiraBugsDialog = (props) => {
    return <TestrunResultModifyDialogWithButton {...props} onlyJiraBugs />;
};

export const TestrunResultFinishDialog = (props) => {
    const { disabled, onSaveTestresultRequest, callbackAfterSave, hasFailedSteps, forceInitialResult } = props;
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState({
        branch: '',
        build: '',
        is_public: false,
    });
    const patchTestResultStatus = useSelector(selectPatchTestResultStatus);
    const testResult = useSelector(selectTestResult);
    const testResultArrMemo = useMemo(() => [testResult], [testResult]);

    const handleSaveTestrunResult = useCallback(
        ({ jiraBugs, comments, result, defectsToToggleLink }) => {
            if (!selectedFilter.branch) {
                setError('branch');
                return false;
            } else if (!selectedFilter.build) {
                setError('build');
                return false;
            }

            onSaveTestresultRequest({
                jiraBugs,
                comments,
                result,
                selectedFilter,
                defectsToToggleLink,
            });

            callbackAfterSave && callbackAfterSave();
            setOpen(false);
        },
        [callbackAfterSave, onSaveTestresultRequest, selectedFilter],
    );

    return (
        <>
            <LoadingButton
                startIcon={<CheckCircleOutlineIcon />}
                sx={utmsButtonSx}
                size="small"
                variant="contained"
                disabled={disabled}
                loading={patchTestResultStatus === REQUEST_STATUS.LOADING}
                loadingPosition="start"
                onClick={() => setOpen(true)}
            >
                {t('to_finish')}
            </LoadingButton>
            <TestrunResultModifyDialog
                {...props}
                open={open}
                setOpen={setOpen}
                handleSave={handleSaveTestrunResult}
                selectedTestRunResultsObjects={testResultArrMemo}
                showDeployFilters
                selectedFilter={selectedFilter}
                setSelectedFilter={setSelectedFilter}
                error={error}
                hasFailedSteps={hasFailedSteps}
                forceInitialResult={forceInitialResult}
            />
        </>
    );
};
